/**
 * Attach clinical metadata to queue cards for Generated Cards UI.
 */

import { EXAM_TYPE_LABELS } from './clinicalOptions.js';

/** Normalize API references: [{ citation }] or string[]. */
export function normalizeReferences(refs) {
    if (!Array.isArray(refs)) return [];
    return refs
        .map((r) => (typeof r === 'string' ? r.trim() : r?.citation?.trim?.() || ''))
        .filter(Boolean);
}

/** Remove embedded "Key points" block from answer text (shown as citations instead). */
export function stripKeyPointsFromAnswer(answer) {
    if (typeof answer !== 'string') return answer;
    const match = answer.match(/\n\nKey points:\s*/i);
    if (!match || match.index == null) return answer.trim();
    return answer.slice(0, match.index).trim();
}

export function getCardReferences(card) {
    return normalizeReferences(card?.references ?? card?.clinical?.references);
}

export function buildClinicalTags(settings, apiCard = {}) {
    const tags = [];
    if (Array.isArray(apiCard.tags)) {
        apiCard.tags.forEach((t) => {
            if (typeof t === 'string' && t.trim()) tags.push(t.trim());
        });
    }
    if (settings?.examType) tags.push(EXAM_TYPE_LABELS[settings.examType] || settings.examType);
    return [...new Set(tags)].slice(0, 8);
}

/**
 * Linkage fields to persist alongside a clinical card: the Q-bank id the card-generation
 * endpoints now return (links a flat card to questions/{id} which holds the per-option
 * rationales, teaching points, and references), an optional Storage download URL for a
 * user-pasted image attached to the card (see uploadCardImage in firebase/config.js — the
 * image itself is never analyzed or reused, just displayed back on this specific card), and
 * the uploaded source document (+ page) a card was generated from, if any (see
 * uploadSourceDocument / ClinicalDocumentStudyModal's "View source" flow).
 *
 * Always emits every key, defaulting to `null` (`?? null`) so plain cards without any of them
 * are unaffected and we never write `undefined` to Firestore. Safe to spread into any card
 * object or Firestore card-doc payload.
 */
export function clinicalCardLinkFields(src) {
    return {
        clinicalQuestionId: src?.clinicalQuestionId ?? null,
        imageUrl: src?.imageUrl ?? null,
        sourceDocumentId: src?.sourceDocumentId ?? null,
        sourceDocumentPage: src?.sourceDocumentPage ?? null,
    };
}

export function enrichClinicalCard(card, settings, apiCard = {}) {
    const references = normalizeReferences(apiCard.references ?? card.references);
    return {
        ...card,
        // Keep the Q-bank link so saved clinical cards yield real rationales in exams.
        clinicalQuestionId: apiCard?.clinicalQuestionId ?? card?.clinicalQuestionId ?? null,
        references,
        source: 'clinical',
        clinical: {
            cardStyle: apiCard.cardStyle || settings?.cardStyle || 'Clinical Vignette',
            difficulty: apiCard.difficulty || null,
            examType: apiCard.examType || settings?.examType,
            references,
            tags: buildClinicalTags(settings, {
                ...apiCard,
                tags: card.clinical?.tags || apiCard.tags,
            }),
        },
    };
}

export function isClinicalQueueCard(card) {
    return card?.source === 'clinical' || !!card?.clinical;
}
